<script lang="ts">
  import { Icon, type IconName } from '$ui/primitives/Icon';
  import { Badge } from '$ui/primitives/Badge';

  let {
    icon,
    label,
    active = false,
    badge,
    onclick,
    collapsed = false,
    class: className = ''
  }: {
    icon: IconName;
    label: string;
    active?: boolean;
    badge?: number;
    onclick?: () => void;
    collapsed?: boolean;
    class?: string;
  } = $props();
</script>

<button
  type="button"
  class="item {className}"
  class:collapsed
  data-active={active || undefined}
  onclick={onclick}
  aria-current={active ? 'page' : undefined}
>
  <span class="icon" aria-hidden="true">
    <Icon name={icon} size={18} />
  </span>
  {#if !collapsed}
    <span class="label">{label}</span>
    {#if badge !== undefined && badge > 0}
      <Badge variant="accent" shape="pill" size="sm">{String(badge)}</Badge>
    {/if}
  {/if}
</button>

<style>
  /* Réplica del .tab-btn del IAGIT.
     Detalles clave: sin height fija, padding-left animado (nudge-right 4px),
     border-left 2px que pasa de transparent a accent en active. */

  .item {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 9px 14px;
    margin-bottom: 4px;
    font-family: var(--font-body, var(--font-sans));
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--color-text-secondary);
    background: transparent;
    border: none;
    border-left: 2px solid transparent;
    border-radius: 0;
    text-align: left;
    cursor: pointer;
    transition:
      background 100ms var(--ease-out),
      color 100ms var(--ease-out),
      border-color 100ms var(--ease-out),
      padding-left 200ms var(--ease-out),
      transform 150ms var(--ease-out);
  }

  .item:active {
    transform: scale(0.97);
  }

  /* Nudge-right: el texto se desplaza 4px a la derecha en hover */
  .item:hover {
    background: var(--color-surface-hover);
    color: var(--color-text-primary);
    padding-left: 18px;
  }

  .item:hover .icon {
    color: var(--color-text-primary);
    transform: scale(1.15) rotate(-5deg);
  }

  .item.collapsed {
    justify-content: center;
    padding: 9px 0;
    gap: 0;
    border-left-width: 0;
    margin-bottom: 8px;
  }

  .item.collapsed:hover {
    padding-left: 0;
    background: var(--color-surface-sunken);
  }

  /* Active: fondo accent-tint + border-left accent + icono accent */
  .item[data-active] {
    background: var(--color-accent-tint);
    border-left-color: var(--color-accent);
    color: var(--color-text-primary);
  }

  .item[data-active] .icon {
    color: var(--color-accent);
  }

  .item:focus-visible {
    outline: var(--focus-ring-width) solid var(--color-focus-ring);
    outline-offset: -2px;
  }

  .icon {
    display: inline-flex;
    align-items: center;
    flex: 0 0 auto;
    color: currentColor;
    transition: color 100ms var(--ease-out), transform 200ms var(--ease-out);
  }

  .label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media (prefers-reduced-motion: reduce) {
    .item {
      transition: background 1ms, color 1ms, border-color 1ms;
    }
    .item:hover {
      padding-left: 14px;
    }
  }
</style>